/*
  File: src/components/dashboard/PadamLeadsSection.jsx
  Purpose: Padam Leads analytics section for Dashboard - displays subscribers, brief demands, and request demos data
  Responsibilities:
  - Display overview cards with key metrics
  - Render charts for data visualization (status distribution, company sizes, trends)
  - Show detailed tables with filtering and pagination
  - Provide export functionality for data
  - Handle loading and error states
  - Embedded version without Navbar/Footer for Dashboard integration
*/

import React, { useState, useMemo, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, 
  faFileAlt, 
  faCalendarCheck, 
  faChartLine,
  faBuilding,
  faEnvelope,
  faPhone,
  faBriefcase,
  faChevronDown,
  faChevronUp,
  faCheckCircle,
  faSpinner,
  faClock,
  faTimesCircle,
  faCalendarAlt,
  faVideo
} from '@fortawesome/free-solid-svg-icons';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar, Line, Doughnut, Pie } from 'react-chartjs-2';
import { apiClient } from '../../auth/api';
import Loader from '../common/Loader';
import CalendlyCalendar from './CalendlyCalendar';
import { fetchCalendlyEvents } from '../../services/calendlyService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler
);

// Stat Card Component
function StatCard({ label, value, icon, trend, change, color = 'blue' }) {
  const colorClasses = {
    blue: 'from-blue-500 to-cyan-500',
    green: 'from-emerald-500 to-green-500',
    purple: 'from-purple-500 to-pink-500',
    orange: 'from-orange-500 to-amber-500',
    red: 'from-red-500 to-pink-500'
  };

  return (
    <div className="relative rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-6 overflow-hidden group hover:border-white/20 transition-all duration-300">
      {/* Background gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br ${colorClasses[color]} opacity-5 group-hover:opacity-10 transition-opacity duration-300`}></div>
      
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-4">
          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center shadow-lg`}>
            <FontAwesomeIcon icon={icon} className="text-white text-xl" />
          </div>
          {trend && (
            <span className={`text-sm font-semibold ${trend === 'up' ? 'text-green-400' : trend === 'down' ? 'text-red-400' : 'text-white/60'}`}>
              {trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'} {change}%
            </span>
          )}
        </div>
        <div className="text-3xl font-bold text-white mb-1">{value}</div>
        <div className="text-sm text-white/60">{label}</div>
      </div>
    </div>
  );
}

// Chart Card Component
function ChartCard({ title, children, className = '' }) {
  return (
    <div className={`rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-6 ${className}`}>
      <h3 className="text-lg font-bold text-white mb-4">{title}</h3>
      <div className="h-64">
        {children}
      </div>
    </div>
  );
}

// Table Component
function DataTable({ title, columns, data, renderRow, showPagination = true, defaultPageSize = 10, showStatusFilter = false, getStatusFromItem }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [itemsPerPage, setItemsPerPage] = useState(defaultPageSize);
  const [statusFilter, setStatusFilter] = useState('all');

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let filteredData = data;
    
    // Apply status filter if enabled
    if (showStatusFilter && statusFilter !== 'all' && getStatusFromItem) {
      filteredData = data.filter(item => {
        const itemStatus = getStatusFromItem(item);
        return itemStatus === statusFilter;
      });
    }
    
    // Apply sorting
    if (!sortConfig.key) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig, statusFilter, showStatusFilter, getStatusFromItem]);

  // Paginate data
  const totalPages = Math.ceil(filteredAndSortedData.length / itemsPerPage);
  const paginatedData = filteredAndSortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Get unique statuses for filter dropdown
  const availableStatuses = useMemo(() => {
    if (!showStatusFilter || !getStatusFromItem) return [];
    
    const statuses = [...new Set(data.map(item => getStatusFromItem(item)))];
    return statuses.sort();
  }, [data, showStatusFilter, getStatusFromItem]);

  // Reset to first page when filter changes
  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
    setCurrentPage(1);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl overflow-hidden">
      <div className="p-6 border-b border-white/10">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white">{title}</h3>
            <p className="text-sm text-white/60 mt-1">
              {statusFilter === 'all' ? `${data.length} total records` : `${filteredAndSortedData.length} of ${data.length} records`}
            </p>
          </div>
          
          {/* Status Filter */}
          {showStatusFilter && availableStatuses.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-white/60">Filter by status:</span>
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => handleStatusFilterChange(e.target.value)}
                  className="px-3 py-2 pr-8 rounded-lg border border-white/20 bg-gray-800 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 hover:bg-gray-700 transition-colors appearance-none cursor-pointer min-w-32"
                >
                  <option value="all" className="bg-gray-800 text-white">All Status</option>
                  {availableStatuses.map(status => (
                    <option key={status} value={status} className="bg-gray-800 text-white">
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </option>
                  ))}
                </select>
                <FontAwesomeIcon 
                  icon={faChevronDown} 
                  className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-white/60 text-xs pointer-events-none"
                />
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-white/5 border-b border-white/10">
            <tr>
              {columns.map((col, idx) => (
                <th
                  key={idx}
                  onClick={() => col.sortable && handleSort(col.key)}
                  className={`px-6 py-4 text-left text-xs font-semibold text-white/80 uppercase tracking-wider ${col.sortable ? 'cursor-pointer hover:text-white' : ''}`}
                >
                  <div className="flex items-center gap-2">
                    {col.label}
                    {col.sortable && sortConfig.key === col.key && (
                      <FontAwesomeIcon 
                        icon={sortConfig.direction === 'asc' ? faChevronUp : faChevronDown} 
                        className="text-xs"
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {paginatedData.map((item, idx) => renderRow(item, idx))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {showPagination && (
        <div className="p-4 border-t border-white/10 space-y-4">
          {/* Page Size Selector and Info */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-sm text-white/60">
                Showing {filteredAndSortedData.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredAndSortedData.length)} of {filteredAndSortedData.length} entries
                {statusFilter !== 'all' && ` (filtered from ${data.length} total)`}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-white/60">Show</span>
                <div className="relative">
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1); // Reset to first page when changing page size
                    }}
                    className="px-3 py-1.5 pr-8 rounded-lg border border-white/20 bg-gray-800 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 hover:bg-gray-700 transition-colors appearance-none cursor-pointer min-w-16"
                  >
                    <option value={5} className="bg-gray-800 text-white">5</option>
                    <option value={10} className="bg-gray-800 text-white">10</option>
                    <option value={20} className="bg-gray-800 text-white">20</option>
                    <option value={50} className="bg-gray-800 text-white">50</option>
                    <option value={100} className="bg-gray-800 text-white">100</option>
                  </select>
                  <FontAwesomeIcon 
                    icon={faChevronDown} 
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-white/60 text-xs pointer-events-none"
                  />
                </div>
                <span className="text-sm text-white/60">per page</span>
              </div>
            </div>
          </div>

          {/* Navigation Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              {/* First Page */}
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all duration-200 ${
                  currentPage === 1 
                    ? 'border-white/10 text-white/30 cursor-not-allowed' 
                    : 'border-white/20 hover:bg-white/10 text-white hover:border-white/40'
                }`}
              >
                First
              </button>

              {/* Previous Page */}
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all duration-200 ${
                  currentPage === 1 
                    ? 'border-white/10 text-white/30 cursor-not-allowed' 
                    : 'border-white/20 hover:bg-white/10 text-white hover:border-white/40'
                }`}
              >
                Previous
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {(() => {
                  const pages = [];
                  const maxVisible = 7;
                  let startPage, endPage;

                  if (totalPages <= maxVisible) {
                    startPage = 1;
                    endPage = totalPages;
                  } else {
                    const maxPagesBeforeCurrentPage = Math.floor(maxVisible / 2);
                    const maxPagesAfterCurrentPage = Math.ceil(maxVisible / 2) - 1;
                    
                    if (currentPage <= maxPagesBeforeCurrentPage) {
                      startPage = 1;
                      endPage = maxVisible;
                    } else if (currentPage + maxPagesAfterCurrentPage >= totalPages) {
                      startPage = totalPages - maxVisible + 1;
                      endPage = totalPages;
                    } else {
                      startPage = currentPage - maxPagesBeforeCurrentPage;
                      endPage = currentPage + maxPagesAfterCurrentPage;
                    }
                  }

                  // Add ellipsis at the beginning
                  if (startPage > 1) {
                    pages.push(
                      <button
                        key={1}
                        onClick={() => setCurrentPage(1)}
                        className="w-10 h-10 rounded-lg border border-white/20 hover:bg-white/10 text-white/70 hover:text-white text-sm font-medium transition-all duration-200"
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pages.push(
                        <span key="ellipsis1" className="px-2 text-white/40">...</span>
                      );
                    }
                  }

                  // Add page numbers
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`w-10 h-10 rounded-lg border text-sm font-medium transition-all duration-200 ${
                          currentPage === i
                            ? 'border-blue-500/50 bg-blue-500/20 text-blue-300 shadow-lg shadow-blue-500/20'
                            : 'border-white/20 hover:bg-white/10 text-white/70 hover:text-white hover:border-white/40'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }

                  // Add ellipsis at the end
                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(
                        <span key="ellipsis2" className="px-2 text-white/40">...</span>
                      );
                    }
                    pages.push(
                      <button
                        key={totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                        className="w-10 h-10 rounded-lg border border-white/20 hover:bg-white/10 text-white/70 hover:text-white text-sm font-medium transition-all duration-200"
                      >
                        {totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}
              </div>

              {/* Next Page */}
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all duration-200 ${
                  currentPage === totalPages 
                    ? 'border-white/10 text-white/30 cursor-not-allowed' 
                    : 'border-white/20 hover:bg-white/10 text-white hover:border-white/40'
                }`}
              >
                Next
              </button>

              {/* Last Page */}
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all duration-200 ${
                  currentPage === totalPages 
                    ? 'border-white/10 text-white/30 cursor-not-allowed' 
                    : 'border-white/20 hover:bg-white/10 text-white hover:border-white/40'
                }`}
              >
                Last
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Status Badge Component
function StatusBadge({ status, type = 'brief' }) {
  const statusConfig = {
    brief: {
      pending: { color: 'yellow', icon: faClock, label: 'Pending' },
      in_progress: { color: 'blue', icon: faSpinner, label: 'In Progress' },
      completed: { color: 'green', icon: faCheckCircle, label: 'Completed' }
    },
    demo: {
      pending: { color: 'yellow', icon: faClock, label: 'Pending' },
      scheduled: { color: 'blue', icon: faCalendarAlt, label: 'Scheduled' },
      completed: { color: 'green', icon: faCheckCircle, label: 'Completed' },
      no_show: { color: 'red', icon: faTimesCircle, label: 'No Show' },
      rescheduled: { color: 'orange', icon: faCalendarCheck, label: 'Rescheduled' }
    }
  };

  const config = statusConfig[type][status] || { color: 'gray', icon: faClock, label: status };
  
  const colorClasses = {
    yellow: 'border-yellow-400/30 bg-yellow-500/15 text-yellow-200',
    blue: 'border-blue-400/30 bg-blue-500/15 text-blue-200',
    green: 'border-emerald-400/30 bg-emerald-500/15 text-emerald-200',
    red: 'border-red-400/30 bg-red-500/15 text-red-200',
    orange: 'border-orange-400/30 bg-orange-500/15 text-orange-200',
    gray: 'border-gray-400/30 bg-gray-500/15 text-gray-200'
  };

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${colorClasses[config.color]}`}>
      <FontAwesomeIcon icon={config.icon} className="text-[10px]" />
      {config.label}
    </span>
  );
}

// Main Component
export default function PadamLintSection() {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subscribers, setSubscribers] = useState([]);
  const [briefDemands, setBriefDemands] = useState([]);
  const [requestDemos, setRequestDemos] = useState([]);
  const [calendlyEvents, setCalendlyEvents] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  // Fetch data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        // Use environment variable for API base URL
        const apiBase = process.env.REACT_APP_PADAM_API_BASE || 'http://localhost:8000';
        const response = await fetch(`${apiBase}/analytics/padam-lint`);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        
        setSubscribers(data.mockSubscribers || []);
        setBriefDemands(data.mockBriefDemands || []);
        setRequestDemos(data.mockRequestDemos || []);
        setAnalytics(data.analytics || {
          subscribers: { total: 0, last7Days: 0, last30Days: 0, growthTrend: 0 },
          briefDemands: { total: 0, subscribed: 0, acceptContact: 0, countryDistribution: {} },
          requestDemos: { total: 0, byStatus: { pending: 0, scheduled: 0, completed: 0, no_show: 0, rescheduled: 0 }, conversionRate: 0, companySizeDistribution: {} }
        });

        // Fetch Calendly events
        const events = await fetchCalendlyEvents({
          minStartTime: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // Last 30 days
          maxStartTime: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)  // Next 90 days
        });
        setCalendlyEvents(events);
      } catch (err) {
        console.error('Failed to fetch Padam Lint data:', err);
        setError('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Subscriber growth trend (last 30 days) - MUST be before conditional returns
  const subscriberTrendData = useMemo(() => {
    const last30Days = Array.from({ length: 30 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - (29 - i));
      return date;
    });

    const counts = last30Days.map(date => {
      return subscribers.filter(sub => {
        const subDate = new Date(sub.created_at);
        return subDate.toDateString() === date.toDateString();
      }).length;
    });

    return {
      labels: last30Days.map(d => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
      datasets: [{
        label: 'New Subscribers',
        data: counts,
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: true,
        tension: 0.4
      }]
    };
  }, [subscribers]);

  // Helper function to get subscriber by ID
  const getSubscriberById = (id) => {
    return subscribers.find(sub => sub.id === id);
  };

  // Show loader while fetching
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader label="Loading Padam Leads data..." />
      </div>
    );
  }

  // Show error if fetch failed
  if (error) {
    return (
      <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-6 text-center">
        <p className="text-red-400 font-semibold mb-2">Error Loading Data</p>
        <p className="text-white/70 text-sm">{error}</p>
      </div>
    );
  }

  // Show message if no analytics data
  if (!analytics) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center">
        <p className="text-white/70">No analytics data available.</p>
      </div>
    );
  }

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Chart data - Brief Demands by Subscription Status
  const briefDemandsStatusData = {
    labels: ['Subscribed', 'Not Subscribed'],
    datasets: [{
      label: 'Brief Demands',
      data: [
        analytics.briefDemands.subscribed,
        analytics.briefDemands.total - analytics.briefDemands.subscribed
      ],
      backgroundColor: [
        'rgba(34, 197, 94, 0.6)',
        'rgba(156, 163, 175, 0.6)'
      ],
      borderColor: [
        'rgb(34, 197, 94)',
        'rgb(156, 163, 175)'
      ],
      borderWidth: 2
    }]
  };

  // Chart data - Brief Demands by Contact Acceptance
  const briefDemandsContactData = {
    labels: ['Accept Contact', 'Declined Contact'],
    datasets: [{
      label: 'Contact Preference',
      data: [
        analytics.briefDemands.acceptContact,
        analytics.briefDemands.total - analytics.briefDemands.acceptContact
      ],
      backgroundColor: [
        'rgba(59, 130, 246, 0.6)',
        'rgba(239, 68, 68, 0.6)'
      ],
      borderColor: [
        'rgb(59, 130, 246)',
        'rgb(239, 68, 68)'
      ],
      borderWidth: 2
    }]
  };

  // Chart data - Country Distribution
  const countryDistributionData = {
    labels: Object.keys(analytics.briefDemands.countryDistribution),
    datasets: [{
      label: 'Countries',
      data: Object.values(analytics.briefDemands.countryDistribution),
      backgroundColor: 'rgba(168, 85, 247, 0.6)',
      borderColor: 'rgb(168, 85, 247)',
      borderWidth: 2
    }]
  };

  const demosStatusData = {
    labels: ['Pending', 'Scheduled', 'Completed', 'No Show', 'Rescheduled'],
    datasets: [{
      label: 'Demo Requests',
      data: [
        analytics.requestDemos.byStatus.pending,
        analytics.requestDemos.byStatus.scheduled,
        analytics.requestDemos.byStatus.completed,
        analytics.requestDemos.byStatus.no_show,
        analytics.requestDemos.byStatus.rescheduled
      ],
      backgroundColor: [
        'rgba(234, 179, 8, 0.6)',
        'rgba(59, 130, 246, 0.6)',
        'rgba(34, 197, 94, 0.6)',
        'rgba(239, 68, 68, 0.6)',
        'rgba(249, 115, 22, 0.6)'
      ],
      borderColor: [
        'rgb(234, 179, 8)',
        'rgb(59, 130, 246)',
        'rgb(34, 197, 94)',
        'rgb(239, 68, 68)',
        'rgb(249, 115, 22)'
      ],
      borderWidth: 2
    }]
  };

  const companySizeData = {
    labels: Object.keys(analytics.requestDemos.companySizeDistribution || {}),
    datasets: [{
      label: 'Companies',
      data: Object.values(analytics.requestDemos.companySizeDistribution || {}),
      backgroundColor: 'rgba(249, 115, 22, 0.6)',
      borderColor: 'rgb(249, 115, 22)',
      borderWidth: 2
    }]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: { color: '#fff', padding: 15 }
      }
    },
    scales: {
      y: {
        ticks: { color: '#ccc' },
        grid: { color: 'rgba(255, 255, 255, 0.1)' }
      },
      x: {
        ticks: { color: '#ccc' },
        grid: { color: 'rgba(255, 255, 255, 0.1)' }
      }
    }
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { color: '#fff', padding: 15 }
      }
    }
  };

  // Table columns
  const subscriberColumns = [
    { key: 'id', label: 'ID', sortable: true },
    { key: 'name', label: 'Name', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'created_at', label: 'Joined', sortable: true }
  ];

  const briefDemandColumns = [
    { key: 'id', label: 'ID', sortable: true },
    { key: 'company_name', label: 'Company', sortable: true },
    { key: 'contact_person', label: 'Contact', sortable: true },
    { key: 'job_title', label: 'Job Title', sortable: true },
    { key: 'country', label: 'Country', sortable: true },
    { key: 'subscribe', label: 'Subscribed', sortable: true },
    { key: 'accept_contact', label: 'Accept Contact', sortable: true },
    { key: 'created_at', label: 'Created', sortable: true }
  ];

  const requestDemoColumns = [
    { key: 'id', label: 'ID', sortable: true },
    { key: 'company_name', label: 'Company', sortable: true },
    { key: 'full_name', label: 'Name', sortable: true },
    { key: 'job_title', label: 'Job Title', sortable: true },
    { key: 'company_size', label: 'Company Size', sortable: true },
    { key: 'demo_status', label: 'Status', sortable: true },
    { key: 'scheduled_date', label: 'Scheduled', sortable: true }
  ];

  const calendlyEventColumns = [
    { key: 'name', label: 'Event Name', sortable: true },
    { key: 'invitee_name', label: 'Attendee', sortable: true },
    { key: 'start_time', label: 'Date & Time', sortable: true },
    { key: 'event_type', label: 'Type', sortable: true },
    { key: 'status', label: 'Status', sortable: true },
    { key: 'location', label: 'Location', sortable: false }
  ];

  return (
    <div className="space-y-8">
      {/* Tab Navigation */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { id: 'overview', label: 'Overview', icon: faChartLine },
          { id: 'subscribers', label: 'Subscribers', icon: faUsers },
          { id: 'briefs', label: 'Brief Demands', icon: faFileAlt },
          { id: 'demos', label: 'Demo Requests', icon: faCalendarCheck },
          { id: 'calendar', label: 'Calendly Events', icon: faCalendarAlt }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all duration-300 whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg shadow-blue-500/30'
                : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white border border-white/10'
            }`}
          >
            <FontAwesomeIcon icon={tab.icon} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-8">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              label="Total Subscribers"
              value={analytics.subscribers.total}
              icon={faUsers}
              color="blue"
              trend={analytics.subscribers.growthTrend > 0 ? 'up' : 'down'}
              change={Math.abs(analytics.subscribers.growthTrend)}
            />
            <StatCard
              label="Brief Demands"
              value={analytics.briefDemands.total}
              icon={faFileAlt}
              color="purple"
            />
            <StatCard
              label="Demo Requests"
              value={calendlyEvents.length}
              icon={faCalendarCheck}
              color="green"
            />
            <StatCard
              label="Conversion Rate"
              value={`${analytics.requestDemos.conversionRate}%`}
              icon={faChartLine}
              color="orange"
            />
          </div>

          {/* Charts Row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Brief Demands - Subscription Status">
              <Doughnut data={briefDemandsStatusData} options={pieChartOptions} />
            </ChartCard>
            <ChartCard title="Brief Demands - Contact Preference">
              <Pie data={briefDemandsContactData} options={pieChartOptions} />
            </ChartCard>
          </div>

          {/* Charts Row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Country Distribution">
              <Bar data={countryDistributionData} options={chartOptions} />
            </ChartCard>
            <ChartCard title="Company Size Distribution (Demos)">
              <Bar data={companySizeData} options={chartOptions} />
            </ChartCard>
          </div>

          {/* Charts Row 3 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Subscriber Growth (Last 30 Days)">
              <Line data={subscriberTrendData} options={chartOptions} />
            </ChartCard>
            <ChartCard title="Demo Requests by Status">
              <Doughnut data={demosStatusData} options={pieChartOptions} />
            </ChartCard>
          </div>


          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="text-2xl font-bold text-white">{analytics.subscribers.last7Days}</div>
              <div className="text-sm text-white/60">New Subscribers (7d)</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="text-2xl font-bold text-white">{analytics.subscribers.last30Days}</div>
              <div className="text-sm text-white/60">New Subscribers (30d)</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="text-2xl font-bold text-emerald-400">{analytics.briefDemands.subscribed}</div>
              <div className="text-sm text-white/60">Subscribed Briefs</div>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="text-2xl font-bold text-blue-400">{analytics.briefDemands.acceptContact}</div>
              <div className="text-sm text-white/60">Accept Contact</div>
            </div>
          </div>
        </div>
      )}

      {/* Subscribers Tab */}
      {activeTab === 'subscribers' && (
        <DataTable
          title="All Subscribers"
          columns={subscriberColumns}
          data={subscribers}
          renderRow={(subscriber) => (
            <tr key={subscriber.id} className="hover:bg-white/5 transition-colors">
              <td className="px-6 py-4 text-sm text-white/80">#{subscriber.id}</td>
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                    {subscriber.name ? subscriber.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
                  </div>
                  <span className="text-sm font-medium text-white">{subscriber.name || 'N/A'}</span>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center gap-2 text-sm text-white/70">
                  <FontAwesomeIcon icon={faEnvelope} className="text-xs" />
                  {subscriber.email}
                </div>
              </td>
              <td className="px-6 py-4 text-sm text-white/70">{formatDate(subscriber.created_at)}</td>
            </tr>
          )}
        />
      )}

      {/* Brief Demands Tab */}
      {activeTab === 'briefs' && (
        <DataTable
          title="Brief Demands"
          columns={briefDemandColumns}
          data={briefDemands}
          renderRow={(brief) => {
            const subscriber = getSubscriberById(brief.subscriber_id);
            return (
              <tr key={brief.id} className="hover:bg-white/5 transition-colors">
                <td className="px-6 py-4 text-sm text-white/80">#{brief.id}</td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-white">
                    <FontAwesomeIcon icon={faBuilding} className="text-xs text-white/60" />
                    {brief.company_name}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-white">{brief.contact_person}</div>
                  <div className="text-xs text-white/60">{brief.contact_email || subscriber?.email}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2 text-sm text-white/70">
                    <FontAwesomeIcon icon={faBriefcase} className="text-xs" />
                    {brief.job_title}
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-white/70">
                  {brief.country || 'Unknown'}
                </td>
                <td className="px-6 py-4">
                  {brief.subscribe ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border border-emerald-400/30 bg-emerald-500/15 text-emerald-200">
                      <FontAwesomeIcon icon={faCheckCircle} className="text-[10px]" />
                      Yes
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border border-gray-400/30 bg-gray-500/15 text-gray-200">
                      <FontAwesomeIcon icon={faTimesCircle} className="text-[10px]" />
                      No
                    </span>
                  )}
                </td>
                <td className="px-6 py-4">
                  {brief.accept_contact ? (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border border-blue-400/30 bg-blue-500/15 text-blue-200">
                      <FontAwesomeIcon icon={faCheckCircle} className="text-[10px]" />
                      Yes
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border border-red-400/30 bg-red-500/15 text-red-200">
                      <FontAwesomeIcon icon={faTimesCircle} className="text-[10px]" />
                      No
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-white/70">{formatDate(brief.created_at)}</td>
              </tr>
            );
          }}
        />
      )}

      {/* Calendar Tab */}
      {activeTab === 'calendar' && (
        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-6">
          <CalendlyCalendar />
        </div>
      )}

      {/* Demo Requests Tab - Now showing Calendly Events */}
      {activeTab === 'demos' && (
        <DataTable
          title="Demo Requests"
          columns={calendlyEventColumns}
          showPagination={true}
          defaultPageSize={10}
          showStatusFilter={true}
          getStatusFromItem={(event) => {
            const startTime = new Date(event.start_time);
            const now = new Date();
            const isUpcoming = startTime > now;
            const isPast = startTime < now;
            
            if (event.status === 'active' && isUpcoming) return 'scheduled';
            if (event.status === 'active' && isPast) return 'completed';
            return event.status || 'unknown';
          }}
          data={calendlyEvents.sort((a, b) => {
            const aStartTime = new Date(a.start_time);
            const bStartTime = new Date(b.start_time);
            const now = new Date();
            
            // Determine status for each event
            const aIsScheduled = a.status === 'active' && aStartTime > now;
            const bIsScheduled = b.status === 'active' && bStartTime > now;
            
            // If one is scheduled and the other isn't, prioritize scheduled
            if (aIsScheduled && !bIsScheduled) return -1;
            if (!aIsScheduled && bIsScheduled) return 1;
            
            // If both have same status, sort by nearest date/time
            return aStartTime - bStartTime;
          })}
          renderRow={(event) => {
            const startTime = new Date(event.start_time);
            const endTime = new Date(event.end_time);
            const isUpcoming = startTime > new Date();
            const isPast = startTime < new Date();
            
            return (
              <tr key={event.id} className="hover:bg-white/5 transition-colors">
                <td className="px-6 py-4">
                  <div className="text-sm font-medium text-white">{event.name}</div>
                  <div className="text-xs text-white/60 mt-1">
                    {startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} - 
                    {endTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-xs">
                      {event.invitee_name ? event.invitee_name.split(' ').map(n => n[0]).join('').toUpperCase() : 'G'}
                    </div>
                    <div>
                      <div className="text-sm text-white">{event.invitee_name || 'Guest'}</div>
                      <div className="text-xs text-white/60 flex items-center gap-2 mt-1">
                        <FontAwesomeIcon icon={faEnvelope} className="text-[10px]" />
                        {event.invitee_email || 'N/A'}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-white">
                    {startTime.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </div>
                  <div className="text-xs text-white/60 flex items-center gap-2 mt-1">
                    <FontAwesomeIcon icon={faClock} className="text-[10px]" />
                    {startTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                  </div>
                  {isUpcoming && (
                    <div className="text-xs text-blue-400 mt-1">
                      {(() => {
                        const now = new Date();
                        const timeDiff = startTime - now;
                        const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
                        const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                        const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
                        
                        if (days > 0) {
                          return `${days}d ${hours}h ${minutes}m`;
                        } else if (hours > 0) {
                          return `${hours}h ${minutes}m`;
                        } else if (minutes > 0) {
                          return `${minutes}m`;
                        } else {
                          return 'Starting soon';
                        }
                      })()}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                    event.event_type === 'video' ? 'border-blue-400/30 bg-blue-500/15 text-blue-200' :
                    event.event_type === 'phone' ? 'border-green-400/30 bg-green-500/15 text-green-200' :
                    'border-purple-400/30 bg-purple-500/15 text-purple-200'
                  }`}>
                    <FontAwesomeIcon 
                      icon={event.event_type === 'video' ? faVideo : event.event_type === 'phone' ? faPhone : faCalendarCheck} 
                      className="text-[10px]" 
                    />
                    {event.event_type === 'video' ? 'Video Call' : 
                     event.event_type === 'phone' ? 'Phone Call' : 
                     'Meeting'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${
                    event.status === 'active' && isUpcoming ? 'border-green-400/30 bg-green-500/15 text-green-200' :
                    event.status === 'active' && isPast ? 'border-blue-400/30 bg-blue-500/15 text-blue-200' :
                    'border-gray-400/30 bg-gray-500/15 text-gray-200'
                  }`}>
                    <FontAwesomeIcon 
                      icon={event.status === 'active' && isUpcoming ? faCalendarCheck : 
                            event.status === 'active' && isPast ? faCheckCircle : 
                            faClock} 
                      className="text-[10px]" 
                    />
                    {event.status === 'active' && isUpcoming ? 'Scheduled' :
                     event.status === 'active' && isPast ? 'Completed' :
                     event.status}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="text-xs text-white/70 max-w-32 truncate" title={event.location}>
                    {event.location}
                  </div>
                </td>
              </tr>
            );
          }}
        />
      )}
    </div>
  );
}